/**
 * Five-session percentage change from daily bars.
 *
 * Replaces the `SAMPLE_FIVE_DAY_PCT` placeholder that shipped on the index
 * tiles — invented constants sitting beside live quote data with nothing
 * marking them as fabricated.
 */

import type { OHLCBar } from '../types/tdSequential';

/**
 * Bars spanned by a five-session move.
 *
 * ⚠️ **Six bars, not five.** Five *intervals* need six *endpoints*: the latest
 * close against the close six bars back. Using five would measure four sessions
 * and produce a number that is wrong by one session while looking entirely
 * plausible — the class of error nothing on screen would reveal.
 */
export const FIVE_DAY_BARS = 6;

/**
 * Percentage change over the last five sessions, or `null` when it cannot be
 * computed.
 *
 * ⚠️ **`null` is not zero.** A tile with too little history must render nothing
 * rather than a flat 0.0%, which would read as "unchanged over five sessions" —
 * a real and quite different claim.
 *
 * @param bars Daily bars, ascending by date, as `useDailyBars` returns them.
 * @returns The change as a percentage (e.g. `1.92` for +1.92%), or `null`.
 */
export function fiveDayChangePct(bars: OHLCBar[] | undefined | null): number | null {
  if (!Array.isArray(bars) || bars.length < FIVE_DAY_BARS) return null;

  const latest = bars[bars.length - 1]?.close;
  const prior = bars[bars.length - FIVE_DAY_BARS]?.close;

  if (!Number.isFinite(latest) || !Number.isFinite(prior)) return null;
  // A zero or negative prior close makes the ratio meaningless, not merely
  // large — guard rather than emit an Infinity that would render as a number.
  if (prior <= 0) return null;

  return ((latest - prior) / prior) * 100;
}
